"use client";

import { HexColorPicker } from "react-colorful";
import { Input } from "./input";
import { Tabs, TabsList, TabsTrigger } from "./tabs";
import { useEffect, useState } from "react";
import { Button } from "./button";
import { generateColorPalette } from "@/lib/colors";
import { PRIMARY_COLOR } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

interface ColorPickerProps {
    mode?: "nice-colors" | "palette" | "gradient";
    initialColor?: string | null;
    onColorChange?: (color: string) => void;
    /** Called immediately when user clicks a palette swatch (for instant apply). */
    onPaletteColorSelect?: (color: string) => void;
    initialPalette?: Record<number, string> | null;
    onPaletteChange?: (palette: Record<number, string> | null) => void;
    allowRegeneratePalette?: boolean;
    /** For gradient mode: initial "from" and "to" colors. */
    initialGradientFrom?: string;
    initialGradientTo?: string;
    /** For gradient mode: called with the full CSS gradient string. */
    onGradientChange?: (gradient: string) => void;
    style?: {
        width?: string; // example: "250px"
    };
}

const niceColors = [
    { name: "red", color: "#ef4444" },
    { name: "orange", color: "#f97316" },
    { name: "amber", color: "#f59e0b" },
    { name: "lime", color: "#84cc16" },
    { name: "green", color: "#22c55e" },
    { name: "emerald", color: "#10b981" },
    { name: "teal", color: "#14b8a6" },
    { name: "cyan", color: "#06b6d4" },
    { name: "sky", color: "#0ea5e9" },
    { name: "blue", color: "#3b82f6" },
    { name: "indigo", color: "#6366f1" },
    { name: "violet", color: "#8b5cf6" },
    { name: "purple", color: "#a855f7" },
    { name: "fuchsia", color: "#d946ef" },
    { name: "pink", color: "#ec4899" },
    { name: "rose", color: "#f43f5e" },
];

export default function ColorPicker({
    mode = "nice-colors",
    initialColor,
    onColorChange,
    onPaletteColorSelect,
    initialPalette,
    onPaletteChange,
    allowRegeneratePalette,
    initialGradientFrom,
    initialGradientTo,
    onGradientChange,
    style,
}: ColorPickerProps) {
    const [color, setColor] = useState<string>(initialColor || PRIMARY_COLOR);
    const [palette, setPalette] = useState<Record<number, string> | null>(
        initialPalette || null
    );

    // Gradient mode state
    const [gradFrom, setGradFrom] = useState(initialGradientFrom || "#2B4D7A");
    const [gradTo, setGradTo] = useState(initialGradientTo || "#142139");
    const [activeStop, setActiveStop] = useState<"from" | "to">("from");

    const { dir } = useLocale();

    useEffect(() => {
        setPalette(initialPalette || null);
    }, [initialPalette]);

    const generatePalette = () => {
        const palette = generateColorPalette(color);
        console.log(palette);
        localStorage.setItem("colorPalette", JSON.stringify(palette));
        localStorage.setItem("color", color);
        if (setPalette) {
            setPalette(palette);
            onPaletteChange?.(palette);
        } else {
            console.log("setPalette not provided");
        }
    };

    // ── Gradient mode ──
    if (mode === "gradient") {
        const activeColor = activeStop === "from" ? gradFrom : gradTo;

        const handlePickerChange = (val: string) => {
            if (activeStop === "from") {
                setGradFrom(val);
                onGradientChange?.(
                    `linear-gradient(135deg, ${val}, ${gradTo})`
                );
            } else {
                setGradTo(val);
                onGradientChange?.(
                    `linear-gradient(135deg, ${gradFrom}, ${val})`
                );
            }
        };

        return (
            <div className="w-[250px] flex flex-col items-start">
                <HexColorPicker
                    color={activeColor}
                    onChange={handlePickerChange}
                    style={{ width: style?.width || "250px" }}
                />

                <div className="w-full grid grid-cols-8 gap-2 my-4">
                    {niceColors.map((c, idx) => (
                        <button
                            key={idx}
                            type="button"
                            style={{
                                backgroundColor: c.color,
                                paddingBottom: "100%",
                            }}
                            className="w-full relative rounded-lg overflow-hidden"
                            onClick={() => handlePickerChange(c.color)}
                        ></button>
                    ))}
                </div>

                {/* From / To tabs above the input */}
                <Tabs
                    dir={dir}
                    value={activeStop}
                    onValueChange={(v) => setActiveStop(v as "from" | "to")}
                    className="w-full mb-1.5"
                >
                    <TabsList className="w-full grid grid-cols-2">
                        <TabsTrigger value="from" className="gap-1.5 text-xs">
                            {t("color-picker.gradient.from")}
                            <span
                                className="w-3 h-3 rounded-sm border border-input shrink-0"
                                style={{ backgroundColor: gradFrom }}
                            />
                        </TabsTrigger>
                        <TabsTrigger value="to" className="gap-1.5 text-xs">
                            {t("color-picker.gradient.to")}
                            <span
                                className="w-3 h-3 rounded-sm border border-input shrink-0"
                                style={{ backgroundColor: gradTo }}
                            />
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Hex input */}
                <div className="w-full flex items-center gap-2 rounded-md border border-input bg-background shadow-sm overflow-hidden">
                    <div
                        style={{ backgroundColor: activeColor }}
                        className="size-6 shrink-0 rounded-md ms-1.5"
                        aria-hidden
                    />
                    <Input
                        dir="ltr"
                        type="text"
                        className="flex-1 min-w-0 border-0 rounded-none shadow-none focus-visible:ring-0 bg-transparent text-foreground text-sm py-2 px-3"
                        value={activeColor || ""}
                        onChange={(e) => handlePickerChange(e.target.value)}
                    />
                </div>
            </div>
        );
    }

    // ── Standard modes (nice-colors / palette) ──
    const colorsToMapOver =
        mode === "palette" && palette
            ? Object.values(palette)
            : niceColors.map((c) => c.color);

    return (
        <div className="w-[250px] flex flex-col items-start">
            <HexColorPicker
                color={color}
                onChange={(val) => {
                    setColor(val);
                    onColorChange?.(val);
                }}
                style={{
                    width: style?.width || "250px",
                }}
            />

            <div className="w-full grid grid-cols-8 gap-2 my-4">
                {colorsToMapOver.map((color, idx) => (
                    <button
                        key={idx}
                        type="button"
                        style={{
                            backgroundColor: color,
                            paddingBottom: "100%",
                        }}
                        className="w-full relative rounded-lg overflow-hidden"
                        onClick={() => {
                            setColor(color);
                            if (onPaletteColorSelect) {
                                onPaletteColorSelect(color);
                            } else {
                                onColorChange?.(color);
                            }
                        }}
                    ></button>
                ))}
            </div>

            <div className="w-full flex flex-col gap-1.5">
                <span className="text-sm text-muted-foreground shrink-0">
                    {t("color-picker.chosen-color.label", undefined, undefined)}
                </span>
                <div className="flex items-center gap-2 rounded-md border border-input bg-background shadow-sm overflow-hidden">
                    {color ? (
                        <div
                            style={{ backgroundColor: color }}
                            className="size-6 shrink-0 rounded-md ms-1.5"
                            aria-hidden
                        />
                    ) : null}
                    <Input
                        dir="ltr"
                        type="text"
                        className="flex-1 min-w-0 border-0 rounded-none shadow-none focus-visible:ring-0 bg-transparent text-foreground text-sm py-2 px-3"
                        value={color || ""}
                        onChange={(e) => {
                            const val = e.target.value;
                            setColor(val);
                            onColorChange?.(val);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                const val = color || "";
                                if (onPaletteColorSelect) {
                                    onPaletteColorSelect(val);
                                } else {
                                    onColorChange?.(val);
                                }
                            }
                        }}
                    />
                </div>
            </div>

            {allowRegeneratePalette && (
                <Button
                    onClick={generatePalette}
                    className="mt-3"
                    size="sm"
                    variant="ai"
                >
                    {t(
                        "color-picker.generate-palette.btn",
                        undefined,
                        undefined
                    )}
                </Button>
            )}
        </div>
    );
}
